import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import API from '../api/axios';
import TaskModal from '../components/TaskModal';
import { useUserPreferences } from '../context/UserPreferencesContext';

/* ------------------------------------------------------------------ config */

// Shared with SettingsPage, which owns the UI for these. Kept here next to the
// consumer, matching how PageNavList exports TOGGLEABLE_PAGES.
export const BOARD_GROUP_OPTIONS = [
  { value: 'status', label: 'Status' },
  { value: 'tag', label: 'Tags' },
  { value: 'dueDate', label: 'Due Date' },
];

export const DEFAULT_BOARD_GROUP_BY = 'status';
export const DEFAULT_BOARD_DAYS = 7;
export const MIN_BOARD_DAYS = 1;
export const MAX_BOARD_DAYS = 30;

export function getBoardGroupBy(preferences) {
  const value = preferences?.boardGroupBy;
  return BOARD_GROUP_OPTIONS.some((o) => o.value === value) ? value : DEFAULT_BOARD_GROUP_BY;
}

export function clampBoardDays(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_BOARD_DAYS;
  return Math.min(MAX_BOARD_DAYS, Math.max(MIN_BOARD_DAYS, Math.round(n)));
}

export function getBoardDayCount(preferences) {
  if (preferences?.boardDayCount === undefined) return DEFAULT_BOARD_DAYS;
  return clampBoardDays(preferences.boardDayCount);
}

const STATUS_COLUMNS = ['Not Started', 'Preparing', 'In Progress', 'Completed'];

const STATUS_COLORS = {
  'Not Started': '#6c757d',
  Preparing: '#e67e22',
  'In Progress': '#1a5fb4',
  Completed: '#198754',
};

const OVERDUE_COLOR = '#d92d20';
const priorityOrder = { High: 0, Medium: 1, Low: 2 };
const priorityBadge = { Low: 'bg-success', Medium: 'bg-warning text-dark', High: 'bg-danger' };

/* ----------------------------------------------------------------- helpers */

// Dates are DATE columns serialised as ISO strings, so the first 10 characters
// are the calendar day. Parsing the whole string with `new Date()` would shift
// it by the UTC offset; the rest of the app slices for the same reason.
function dueKey(task) {
  return task.end_date ? task.end_date.slice(0, 10) : null;
}

function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.slice(0, 10).split('-');
  return new Date(year, month - 1, day).toLocaleDateString();
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function addDays(date, n) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
}

function isoDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Tag colours are user-chosen, so a column header can land anywhere on the
// range. Pick black or white text by WCAG relative luminance instead of
// assuming white reads on every swatch.
function readableTextColor(hex) {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || '');
  if (!match) return '#ffffff';
  let h = match[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const channel = (offset) => {
    const c = parseInt(h.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  return luminance > 0.45 ? '#101828' : '#ffffff';
}

function sortColumn(tasks) {
  return [...tasks].sort((a, b) => {
    const pa = a.priority ? priorityOrder[a.priority] : 99;
    const pb = b.priority ? priorityOrder[b.priority] : 99;
    if (pa !== pb) return pa - pb;
    const da = dueKey(a);
    const db = dueKey(b);
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return da < db ? -1 : da > db ? 1 : 0;
  });
}

/* -------------------------------------------------------- column factories */

/*
 * A column is:
 *   key         unique id, also the drag source marker
 *   title       header label (subtitle optional, used by day columns)
 *   headerBg/Fg header colours; `soft` swaps the saturated header for a
 *               bordered neutral one
 *   tasks       already sorted
 *   seed        partial task used to prefill "+ new task here"; null hides the
 *               add button
 *   dropUpdates (task, fromKey) => updates for PATCH /tasks/bulk, or null when
 *               the drop is a no-op. Absent entirely => not a drop target.
 */

function buildStatusColumns(tasks) {
  return STATUS_COLUMNS.map((status) => ({
    key: `status:${status}`,
    title: status,
    headerBg: STATUS_COLORS[status],
    headerFg: '#ffffff',
    // Matches the board's long-standing behaviour: On Hold has no column, so
    // those tasks are not shown when grouping by status.
    tasks: sortColumn(tasks.filter((t) => (t.status || 'Not Started') === status)),
    seed: { status },
    dropUpdates: (task) => (task.status === status ? null : { status }),
  }));
}

// PATCH /tasks/bulk replaces a task's tags wholesale, so a drop sends the full
// resulting list. Moving between two tag columns removes the source tag and
// adds the target one; the task keeps any tags belonging to neither column.
function retagUpdates(task, fromKey, targetTagId) {
  const current = (task.tags || []).map((t) => t.id);
  const fromTagId =
    fromKey && fromKey.startsWith('tag:') && fromKey !== 'tag:none'
      ? fromKey.slice('tag:'.length)
      : null;

  let next = current;
  if (fromTagId) next = next.filter((id) => String(id) !== fromTagId);
  if (targetTagId !== null && !next.some((id) => String(id) === String(targetTagId))) {
    next = [...next, targetTagId];
  }

  const unchanged =
    next.length === current.length && next.every((id) => current.includes(id));
  return unchanged ? null : { tag_ids: next };
}

function buildTagColumns(tasks, tags) {
  const columns = tags.map((tag) => ({
    key: `tag:${tag.id}`,
    title: tag.name,
    headerBg: tag.color || '#6c757d',
    headerFg: readableTextColor(tag.color),
    tasks: sortColumn(tasks.filter((t) => (t.tags || []).some((x) => x.id === tag.id))),
    seed: { tags: [tag] },
    dropUpdates: (task, fromKey) => retagUpdates(task, fromKey, tag.id),
  }));

  columns.push({
    key: 'tag:none',
    title: 'Untagged',
    soft: true,
    tasks: sortColumn(tasks.filter((t) => !(t.tags || []).length)),
    seed: { tags: [] },
    dropUpdates: (task, fromKey) => retagUpdates(task, fromKey, null),
  });

  return columns;
}

function buildDueDateColumns(tasks, dayCount) {
  const today = startOfToday();
  const todayIso = isoDate(today);
  const days = Array.from({ length: dayCount }, (_, i) => addDays(today, i));

  // ISO day strings sort lexicographically, so plain comparison is enough.
  const byDay = new Map(days.map((d) => [isoDate(d), []]));
  const overdue = [];
  const later = [];
  const undated = [];

  for (const task of tasks) {
    const key = dueKey(task);
    if (!key) undated.push(task);
    else if (byDay.has(key)) byDay.get(key).push(task);
    else if (key < todayIso) overdue.push(task);
    else later.push(task);
  }

  // Overdue and Later have no single date to assign, so they are deliberately
  // not drop targets -- but they still have to exist, or dated tasks outside
  // the window would vanish from the board.
  const columns = [
    {
      key: 'due:overdue',
      title: 'Overdue',
      headerBg: OVERDUE_COLOR,
      headerFg: '#ffffff',
      tasks: sortColumn(overdue),
      seed: null,
    },
  ];

  for (const day of days) {
    const iso = isoDate(day);
    const isToday = iso === todayIso;
    const weekend = day.getDay() === 0 || day.getDay() === 6;
    columns.push({
      key: `due:${iso}`,
      title: isToday ? 'Today' : day.toLocaleDateString(undefined, { weekday: 'short' }),
      subtitle: day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      soft: !isToday,
      headerBg: isToday ? 'var(--fx-primary)' : undefined,
      headerFg: isToday ? '#ffffff' : undefined,
      muted: weekend && !isToday,
      isToday,
      tasks: sortColumn(byDay.get(iso)),
      seed: { end_date: iso },
      dropUpdates: (task) => (dueKey(task) === iso ? null : { end_date: iso }),
    });
  }

  columns.push({
    key: 'due:later',
    title: 'Later',
    soft: true,
    tasks: sortColumn(later),
    seed: null,
  });

  columns.push({
    key: 'due:none',
    title: 'No Due Date',
    soft: true,
    tasks: sortColumn(undated),
    seed: {},
    // '' is mapped to NULL by PATCH /tasks/bulk.
    dropUpdates: (task) => (dueKey(task) === null ? null : { end_date: '' }),
  });

  return columns;
}

/* -------------------------------------------------------------- the board - */

export default function BoardPage() {
  const { preferences } = useUserPreferences();
  const groupBy = getBoardGroupBy(preferences);
  const dayCount = getBoardDayCount(preferences);

  const [tasks, setTasks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [dragged, setDragged] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const scrollerRef = useRef(null);
  const todayRef = useRef(null);

  const fetchTasks = useCallback(async () => {
    const { data } = await API.get('/tasks');
    setTasks(data);
  }, []);

  useEffect(() => {
    fetchTasks();
    API.get('/folders').then(({ data }) => setFolders(data));
    API.get('/tags').then(({ data }) => setTags(data));
  }, [fetchTasks]);

  const columns = useMemo(() => {
    if (groupBy === 'tag') return buildTagColumns(tasks, tags);
    if (groupBy === 'dueDate') return buildDueDateColumns(tasks, dayCount);
    return buildStatusColumns(tasks);
  }, [groupBy, dayCount, tasks, tags]);

  // Day boards start scrolled past Overdue so today is the first thing seen.
  // Deliberately not keyed on `tasks` -- re-running on every refetch would
  // yank the board back while the user is scrolled elsewhere.
  useEffect(() => {
    if (groupBy !== 'dueDate') return;
    const scroller = scrollerRef.current;
    const todayEl = todayRef.current;
    if (!scroller || !todayEl) return;
    scroller.scrollLeft = Math.max(0, todayEl.offsetLeft - 16);
  }, [groupBy, dayCount]);

  const handleDragStart = (e, taskId, fromKey) => {
    setDragged({ taskId, fromKey });
    e.dataTransfer.effectAllowed = 'move';
    // Firefox refuses to start a drag unless some data is set. Deliberately
    // not the MIME the folder tree listens for -- board cards are not meant to
    // be droppable onto folders.
    e.dataTransfer.setData('text/plain', String(taskId));
  };

  const handleDragOver = (e, column) => {
    if (!column.dropUpdates) {
      e.dataTransfer.dropEffect = 'none';
      return; // no preventDefault => not a drop target
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverKey !== column.key) setDragOverKey(column.key);
  };

  const handleDragLeave = (e) => {
    // dragleave also fires when crossing into a child element.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverKey(null);
  };

  const handleDrop = async (e, column) => {
    e.preventDefault();
    setDragOverKey(null);
    if (!dragged || !column.dropUpdates) return;

    const task = tasks.find((t) => t.id === dragged.taskId);
    const fromKey = dragged.fromKey;
    setDragged(null);
    if (!task) return;

    const updates = column.dropUpdates(task, fromKey);
    if (!updates) return;

    try {
      await API.patch('/tasks/bulk', { ids: [task.id], updates });
      fetchTasks();
    } catch {
      // ignore
    }
  };

  const handleEdit = (task) => {
    setEditTask(task);
    setShowModal(true);
  };

  // `seed` is shaped like a task, which is what TaskModal reads to prefill its
  // form -- so the new task lands in the column it was created from.
  const handleAdd = (seed) => {
    setEditTask(seed);
    setShowModal(true);
  };

  const handleSave = async (taskData) => {
    if (editTask?.id) {
      await API.put(`/tasks/${editTask.id}`, taskData);
    } else {
      await API.post('/tasks', taskData);
    }
    setShowModal(false);
    setEditTask(null);
    fetchTasks();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await API.delete(`/tasks/${id}`);
    setShowModal(false);
    setEditTask(null);
    fetchTasks();
  };

  // Few columns share the width; many get a fixed width and scroll instead.
  const fluid = columns.length <= 5;
  const columnStyle = fluid
    ? { flex: '1 1 0', minWidth: 220, height: '100%' }
    : { flex: '0 0 250px', height: '100%' };

  return (
    <div className="d-flex flex-column h-100">
      {groupBy === 'tag' && tags.length === 0 && (
        <div className="alert alert-info mx-3 mt-3 mb-0">
          The board is grouped by tags, but no tags exist yet. Create some in Settings.
        </div>
      )}

      <div
        ref={scrollerRef}
        className="d-flex flex-grow-1 px-3 py-3 gap-3"
        style={{ minHeight: 0, overflowX: 'auto', position: 'relative' }}
      >
        {columns.map((col) => {
          const droppable = Boolean(col.dropUpdates);
          const isDragOver = dragOverKey === col.key;
          return (
            <div
              key={col.key}
              ref={col.isToday ? todayRef : undefined}
              className="d-flex flex-column"
              style={columnStyle}
              onDragOver={(e) => handleDragOver(e, col)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col)}
            >
              <div
                className={`rounded-top px-3 py-2 d-flex justify-content-between align-items-center ${
                  col.soft ? 'border border-bottom-0' : ''
                }`}
                style={{
                  backgroundColor: col.soft ? 'var(--fx-subtle)' : col.headerBg,
                  color: col.soft ? 'var(--fx-text)' : col.headerFg,
                  opacity: col.muted ? 0.75 : 1,
                }}
              >
                <div className="d-flex align-items-center gap-2 text-truncate">
                  {col.seed && (
                    <button
                      className="btn btn-sm p-0 d-flex align-items-center justify-content-center flex-shrink-0"
                      title={`Add task to ${col.title}`}
                      onClick={() => handleAdd(col.seed)}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: col.soft ? 'var(--fx-hover)' : 'rgba(255,255,255,0.3)',
                        color: 'inherit',
                        fontSize: '1rem',
                        lineHeight: 1,
                        border: 'none',
                      }}
                    >
                      +
                    </button>
                  )}
                  <div className="text-truncate">
                    <strong className="d-block text-truncate">{col.title}</strong>
                    {col.subtitle && (
                      <small style={{ opacity: 0.75 }}>{col.subtitle}</small>
                    )}
                  </div>
                </div>
                <span className="badge bg-light text-dark flex-shrink-0">{col.tasks.length}</span>
              </div>

              <div
                className="flex-grow-1 overflow-auto border border-top-0 rounded-bottom p-2"
                style={{
                  backgroundColor: 'var(--bs-tertiary-bg, #f8f9fa)',
                  outline: isDragOver ? '2px solid var(--fx-primary)' : 'none',
                  outlineOffset: -2,
                  transition: 'outline-color 0.12s ease',
                }}
              >
                {col.tasks.map((task) => (
                  <div
                    key={task.id}
                    className="card mb-2 shadow-sm"
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id, col.key)}
                    onDragEnd={() => { setDragged(null); setDragOverKey(null); }}
                    style={{ cursor: 'grab', opacity: dragged?.taskId === task.id ? 0.5 : 1 }}
                  >
                    <div className="card-body p-2" onClick={() => handleEdit(task)} style={{ cursor: 'pointer' }}>
                      <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                        {task.name}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                        {task.priority && (
                          <span className={`badge ${priorityBadge[task.priority]}`} style={{ fontSize: '0.7rem' }}>
                            {task.priority}
                          </span>
                        )}
                        {/* Status is otherwise only visible via the column itself. */}
                        {groupBy !== 'status' && task.status && (
                          <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                            {task.status}
                          </span>
                        )}
                        {task.end_date && (
                          <small
                            className={
                              dueKey(task) < isoDate(startOfToday()) && task.status !== 'Completed'
                                ? 'text-danger'
                                : 'text-muted'
                            }
                          >
                            {formatDateLocal(task.end_date)}
                          </small>
                        )}
                      </div>
                      {task.folder_name && (
                        <div className="mt-1">
                          <small className="text-muted">
                            {'📁'} {task.folder_name}
                          </small>
                        </div>
                      )}
                      {task.tags && task.tags.length > 0 && (
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {task.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="badge"
                              style={{
                                backgroundColor: tag.color,
                                color: readableTextColor(tag.color),
                                fontSize: '0.65rem',
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {col.tasks.length === 0 && (
                  <div className="text-muted text-center py-4" style={{ fontSize: '0.85rem' }}>
                    {droppable ? 'No tasks' : '—'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <TaskModal
          task={editTask}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => { setShowModal(false); setEditTask(null); }}
          folders={folders}
          currentFolderId={editTask?.folder_id}
          tags={tags}
        />
      )}
    </div>
  );
}
